import React from 'react'
import classNames from 'classnames'
import {
  ImageResolutionsProps,
  RenderImgSourceProps
} from './ImageResolutions.d'
import { Card } from '@toasttab/shared/src/utils/craftUtils'

const getResponsiveWidths = (minWidth: string | null) => {
  if (!minWidth) return { w1x: 375, w2x: 750 }
  const width = parseInt(minWidth)
  return {
    w1x: width,
    w2x: width * 2
  }
}

const buildSrcSet = (
  url: string,
  minWidth: string | null,
  format: 'avif' | null = null
) => {
  if (!url) return ''
  const baseUrl = url.split('?')[0]
  const { w1x, w2x } = getResponsiveWidths(minWidth)
  return `${baseUrl}?${
    format ? `fm=${format}&` : ''
  }w=${w1x}&fit=scale ${w1x}w, ${baseUrl}?${
    format ? `fm=${format}&` : ''
  }w=${w2x}&fit=scale ${w2x}w`
}

const RenderImgSource = ({
  altText,
  classes,
  dataTestId,
  image,
  minWidth,
  fileWidth,
  El
}: RenderImgSourceProps) => {
  if (!fileWidth) {
    fileWidth = minWidth
  }
  const isSvg = image.extension === 'svg'
  const imgSrc =
    image.optimizedImages && !isSvg ? image.optimizedImages.srcAvif : image.url
  const unoptimizedImgType = isSvg ? 'svg+xml' : image.extension
  const imgType = image.optimizedImages && !isSvg ? 'avif' : unoptimizedImgType
  const maxWidth = parseInt(minWidth) * 2
  const imgAlt = altText ? altText : image.title
  if (El === 'img') {
    return (
      <>
        <source
          className={classes}
          srcSet={buildSrcSet(imgSrc, fileWidth, 'avif')}
          type={`image/${imgType}`}
          sizes={`${
            minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null
          }`}
        />
        <img
          alt={imgAlt}
          className={classes}
          data-testid={dataTestId}
          src={image.url}
          srcSet={buildSrcSet(image.url, fileWidth)}
          typeof={image.extension}
          sizes={`${
            minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null
          }`}
        />
      </>
    )
  }
  return (
    <>
      <El
        className={classes}
        data-testid={dataTestId}
        media={minWidth ? `(min-width: ${minWidth})` : null}
        sizes={`${minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null}`}
        srcSet={buildSrcSet(imgSrc, fileWidth, 'avif')}
        type={`image/${imgType}`}
      />
      <El
        className={classes}
        media={minWidth ? `(min-width: ${minWidth})` : null}
        sizes={`${minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null}`}
        srcSet={buildSrcSet(image.url, fileWidth)}
        type={image.extension}
      />
    </>
  )
}

const ImageResolutions = ({
  imageAlt,
  images,
  mobileBleed = false,
  pictureClasses = '',
  classes,
  borderRadius = 'None',
  fileWidths = {
    desktop: '1024px',
    tablet: '768px',
    mobile: ''
  }
}: ImageResolutionsProps) => {
  const imageCorners = Card.borderRadius(borderRadius)
  const imgClasses = classNames(
    classes,
    imageCorners,
    'overflow-hidden lazyload',
    {
      '-mx-4 md:mx-0': mobileBleed
    }
  )

  const imgResolutions = images?.imageResolutions && images.imageResolutions[0]

  if (!imgResolutions) return null

  const desktop =
    imgResolutions.desktop && imgResolutions.desktop.length > 0
      ? imgResolutions.desktop[0]
      : null
  const tablet =
    imgResolutions.tablet && imgResolutions.tablet.length > 0
      ? imgResolutions.tablet[0]
      : null
  const mobile =
    imgResolutions.mobile && imgResolutions.mobile.length > 0
      ? imgResolutions.mobile[0]
      : null
  const altText = imageAlt
    ? imageAlt
    : desktop?.description
    ? desktop.description
    : desktop?.title

  return (
    desktop && (
      <picture className={pictureClasses} data-testid='mds-imageResolutions'>
        <RenderImgSource
          El='source'
          dataTestId='mds-imageResolutions-source-desktop'
          fileWidth={fileWidths?.desktop}
          image={desktop}
          minWidth='1024px'
        />
        <RenderImgSource
          El='source'
          dataTestId='mds-imageResolutions-source-tablet'
          fileWidth={fileWidths?.tablet}
          image={tablet || desktop}
          minWidth='768px'
        />
        <RenderImgSource
          El='img'
          altText={altText}
          classes={imgClasses}
          dataTestId='mds-imageResolutions-img'
          fileWidth={fileWidths?.mobile}
          image={mobile || desktop}
        />
      </picture>
    )
  )
}

export default ImageResolutions
